import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Tooltip, useMediaQuery } from "@mui/material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { getCatalogModelDetail } from "../machineCatalogSlice";
import { MachineCatalogState, CatalogSpecSection, CatalogSpecItem } from "../models/models";
import { dt } from "../utils/dt";
import { convertSpec } from "../utils/unitConversion";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import useTranslation from "../../../shared/hooks/useTranslation";

interface MachineSpecsProps {
  catalogModelId: string;
}

/* -- Encoding & language detection ---------------------------------------- */

/**
 * Detects mojibake (garbled encoding) in a string.
 * Czech text encoded as latin-1 then decoded as UTF-8 produces sequences
 * like Å¾, Ã¡, Ã­, Å™ etc. We look for these common mojibake patterns.
 */
function hasMojibake(text: string): boolean {
  if (!text) return false;

  // Common mojibake sequences from UTF-8 -> Latin-1 mis-encoding of Czech/Slovak
  const mojibakePatterns = [
    /[\xC3][\x80-\xBF]/,       // Ã followed by high byte (á→Ã¡, í→Ã­, etc.)
    /[\xC5][\x80-\xBF]/,       // Å followed by high byte (ž→Å¾, ř→Å™, etc.)
    /[\xC4][\x80-\xBF]/,       // Ä followed by high byte (č→Ä\x8D, ě→Ä\x9B, etc.)
    /Ã¡|Ã¨|Ã­|Ã©|Ãº|Ã½|Ã¼|Ã¶|Ã¤|Ã®|Ã´|Ã»/, // double-byte mojibake for vowels
    /Å¾|Å™|Å¡|Å¥|Å\u0088|Å¯|Å¤|Å¸/,           // double-byte mojibake for consonants
    /Ä\u008D|Ä\u009B|Ä¾|Ä½|Ä\u0090|Ä\u008E/,   // more Czech/Slovak consonant mojibake
  ];

  // Consecutive non-ASCII byte-like characters are a strong mojibake signal
  const suspiciousChars = text.match(/[\u00C0-\u00FF]{2,}/g);
  if (suspiciousChars && suspiciousChars.length > 1) return true;

  // Many isolated Ã, Å, Ä characters signal mojibake (these are the first byte
  // of a double-byte UTF-8 sequence mis-decoded as Latin-1)
  const isolatedHighBytes = (text.match(/[ÃÅÄ]/g) || []).length;
  if (isolatedHighBytes >= 3) return true;

  for (const pattern of mojibakePatterns) {
    if (pattern.test(text)) return true;
  }
  return false;
}

/**
 * Detects whether a text block is likely Czech language.
 * Czech-specific characters (ě, ř, ů, ň, ď, ť) rarely appear in English or
 * other Western European languages. We also count common Czech diacritics
 * (š, č, ž, ý, á, í, é, ú) and require a minimum density to avoid false
 * positives on e.g. French or German text that shares some accented chars.
 */
function isCzechText(text: string): boolean {
  if (!text) return false;
  // Characters that are unique to Czech (and Slovak) — very strong signal
  const uniqueCzechMatches = text.match(/[ěřůňďťĚŘŮŇĎŤ]/g);
  if (uniqueCzechMatches && uniqueCzechMatches.length >= 2) return true;

  // Broader set of Czech diacritics — need higher count to distinguish
  // from French/German/Spanish which share some of these
  const allCzechMatches = text.match(/[ěščřžýáíéúůďťňĚŠČŘŽÝÁÍÉÚŮĎŤŇ]/g);
  if (allCzechMatches && allCzechMatches.length >= 3) return true;

  return false;
}

/* -- Section classification ---------------------------------------------- */

/** Section names that are treated as description blocks */
const DESCRIPTION_SECTIONS = new Set([
  "description",
  "description (en)",
  "description (cz)",
  "vertical machining center",
  "horizontal machining center",
  "turning center",
  "cylindrical grinding machine",
  "surface grinding machine",
  "sinker edm",
  "wire edm",
  "multi-axis machining center",
  "5-axis machining center",
  "boring mill",
  "combination lathe",
  "vertical turning center",
  "horizontal turning center",
  "centerless grinding machine",
  "internal grinding machine",
  "tool and cutter grinding machine",
  "jig grinding machine",
  "creep feed grinding machine",
  "profile grinding machine",
  "gear grinding machine",
  "double disk grinding machine",
]);

const EQUIPMENT_SECTIONS = new Set([
  "standard equipment",
  "optional equipment",
]);

/**
 * Additional heuristic: a section is "description-like" if most of its specs
 * have empty keys (i.e., the section is prose, not key-value pairs).
 * This catches machine-type headings that aren't in the static set.
 */
function isDescriptionLikeSection(section: CatalogSpecSection): boolean {
  if (section.specs.length === 0) return false;
  const emptyKeyCount = section.specs.filter(
    (s) => !s.specKey || s.specKey.trim().length === 0,
  ).length;
  // If more than half the specs have empty keys, treat as description
  return emptyKeyCount > section.specs.length / 2;
}

/**
 * Section ordering hierarchy. Lower number = shown first.
 * Sections not listed get a high default number (shown after everything else).
 */
function getSectionOrder(sectionName: string): number {
  const lower = sectionName.toLowerCase();

  // Description sections - always first
  if (DESCRIPTION_SECTIONS.has(lower)) return 0;

  // Core parameters
  if (lower === "axes & travels") return 10;
  if (lower === "tooling") return 11;
  if (lower === "feed rates") return 12;
  if (lower === "accuracy") return 13;
  if (lower === "electrical") return 14;
  if (lower === "motors") return 15;
  if (lower === "working capacity") return 16;
  if (lower === "turret") return 17;

  // Spindle data
  if (lower === "spindles" || lower === "spindle(s)") return 20;
  if (lower === "headstock") return 21;
  if (lower === "single spindle") return 22;
  if (lower === "multi spindle") return 23;
  if (lower.includes("grinding wheel") || lower.includes("spindle")) return 24;

  // Work support
  if (lower === "work support" || lower === "table/work support") return 30;
  if (lower === "workpiece envelope") return 31;

  // Control
  if (lower === "control") return 40;
  if (lower === "wire data") return 41;

  // Equipment
  if (lower === "standard equipment") return 50;
  if (lower === "optional equipment") return 51;

  // Options
  if (lower === "options") return 60;

  // Everything else
  return 100;
}

/** Group key for looking up group-specific tokens */
type GroupKey = "core" | "spindle" | "workSupport" | "control" | "equipment" | "options" | null;

function getSectionGroupKey(sectionName: string): GroupKey {
  const order = getSectionOrder(sectionName);
  if (order === 0) return null; // descriptions don't show group labels
  if (order >= 10 && order <= 17) return "core";
  if (order >= 20 && order <= 24) return "spindle";
  if (order >= 30 && order <= 31) return "workSupport";
  if (order >= 40 && order <= 41) return "control";
  if (order >= 50 && order <= 51) return "equipment";
  if (order === 60) return "options";
  return null;
}

/** Group labels for section groups */
function getSectionGroupLabel(sectionName: string, t: (k: string, o?: any) => any): string | null {
  const key = getSectionGroupKey(sectionName);
  if (!key) return null;
  const labelMap: Record<string, string> = {
    core: t("specGroupCore"),
    spindle: t("specGroupSpindle"),
    workSupport: t("specGroupWorkSupport"),
    control: t("specGroupControl"),
    equipment: t("specGroupEquipment"),
    options: t("specGroupOptions"),
  };
  return labelMap[key] || null;
}

/** Get group-specific accent and background colors */
function getGroupColors(groupKey: GroupKey): { accent: string; bg: string } {
  if (!groupKey) return { accent: dt.textMuted, bg: "transparent" };
  const map: Record<string, { accent: string; bg: string }> = {
    core: dt.specs.groupCore,
    spindle: dt.specs.groupSpindle,
    workSupport: dt.specs.groupWorkSupport,
    control: dt.specs.groupControl,
    equipment: dt.specs.groupEquipment,
    options: dt.specs.groupOptions,
  };
  return map[groupKey] || { accent: dt.textMuted, bg: "transparent" };
}

/** Get group-specific icon component */
function getGroupIcon(groupKey: GroupKey): React.ReactElement | null {
  if (!groupKey) return null;
  const iconSx = { fontSize: 15, opacity: 0.85 };
  switch (groupKey) {
    case "core": return <TuneOutlinedIcon sx={iconSx} />;
    case "spindle": return <PrecisionManufacturingOutlinedIcon sx={iconSx} />;
    case "workSupport": return <SettingsOutlinedIcon sx={iconSx} />;
    case "control": return <MemoryOutlinedIcon sx={iconSx} />;
    case "equipment": return <BuildOutlinedIcon sx={iconSx} />;
    case "options": return <ExtensionOutlinedIcon sx={iconSx} />;
    default: return null;
  }
}

/* -- Section classification helpers -------------------------------------- */

function isDescriptionSection(sectionName: string): boolean {
  return DESCRIPTION_SECTIONS.has(sectionName.toLowerCase());
}

function isEquipmentSection(sectionName: string): boolean {
  return EQUIPMENT_SECTIONS.has(sectionName.toLowerCase());
}

function isStandardEquipment(sectionName: string): boolean {
  return sectionName.toLowerCase() === "standard equipment";
}

/* -- Sub-components ------------------------------------------------------ */

/* -- Encoding Fallback Badge --------------------------------------------- */
const EncodingFallbackBadge: React.FC<{ t: (k: string, o?: any) => any }> = ({ t }) => (
  <Tooltip title={t("encodingFallbackNotice")} arrow enterDelay={200}>
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "2px 8px",
        borderRadius: dt.specs.pillRadius,
        backgroundColor: dt.specs.encodingBadgeBg,
        border: `1px solid ${dt.specs.encodingBadgeBorder}`,
        cursor: "help",
        ml: 1,
        flexShrink: 0,
      }}
    >
      <InfoOutlinedIcon
        sx={{
          fontSize: 12,
          color: dt.specs.encodingBadgeText,
        }}
      />
      <Box
        sx={{
          fontSize: "10px",
          fontWeight: 600,
          fontFamily: dt.font,
          color: dt.specs.encodingBadgeText,
          letterSpacing: "0.2px",
          whiteSpace: "nowrap",
        }}
      >
        EN
      </Box>
    </Box>
  </Tooltip>
);

/* -- Description Block --------------------------------------------------- */
const DescriptionBlock: React.FC<{
  descriptionText: string;
  sectionLabel: string;
  idx: number;
  showEncodingFallback?: boolean;
  t: (k: string, o?: any) => any;
}> = ({ descriptionText, sectionLabel, idx, showEncodingFallback, t }) => {
  if (!descriptionText.trim()) return null;

  const paragraphs = descriptionText.split(/\n\s*\n/).filter((p) => p.trim());

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: dt.radiusSm,
        border: `1px solid ${dt.cardBorder}`,
        overflow: "hidden",
        animation: "fadeUp 0.35s ease-out both",
        animationDelay: `${idx * 80}ms`,
      }}
    >
      {/* Description header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          padding: "10px 16px",
          backgroundColor: dt.specs.descBg,
          borderBottom: `1px solid ${dt.cardBorder}`,
        }}
      >
        <DescriptionOutlinedIcon
          sx={{
            fontSize: 16,
            color: dt.specs.descAccent,
          }}
        />
        <Box
          sx={{
            fontSize: "12px",
            fontWeight: 700,
            fontFamily: dt.font,
            color: dt.specs.descAccent,
            textTransform: "uppercase",
            letterSpacing: "0.6px",
          }}
        >
          {sectionLabel}
        </Box>
        {showEncodingFallback && <EncodingFallbackBadge t={t} />}
      </Box>

      {/* Description body -- improved paragraph handling for Czech text */}
      <Box
        sx={{
          padding: "16px 20px",
          backgroundColor: dt.specs.descBodyBg,
          borderLeft: `3px solid ${dt.specs.descBg}`,
        }}
      >
        {paragraphs.map((paragraph, pIdx) => (
          <Box
            key={pIdx}
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              fontFamily: dt.font,
              color: dt.textPrimary,
              lineHeight: 1.8,
              whiteSpace: "pre-line",
              wordBreak: "break-word",
              overflowWrap: "break-word",
              letterSpacing: "0.15px",
              mb: pIdx < paragraphs.length - 1 ? 1.5 : 0,
              pl: 0.5,
            }}
          >
            {paragraph.trim()}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/* -- Equipment List ------------------------------------------------------ */
const EquipmentList: React.FC<{
  section: CatalogSpecSection;
  idx: number;
  isPhone: boolean;
  isTablet: boolean;
  t: (k: string, o?: any) => any;
}> = ({ section, idx, isPhone, isTablet, t }) => {
  const [expanded, setExpanded] = useState(true);
  const isStandard = isStandardEquipment(section.sectionName);

  // Filter items: use specKey as the equipment item name
  // Only include items where the key is meaningful (non-empty)
  const items = section.specs
    .map((s) => s.specKey)
    .filter((k) => k && k.trim().length > 0);

  if (items.length === 0) return null;

  const countLabel = isStandard
    ? t("equipIncluded", { count: items.length })
    : t("equipOptional", { count: items.length });

  return (
    <Box
      sx={{
        mb: 1.5,
        border: `1px solid ${isStandard ? dt.specs.equipStandardBorder : dt.specs.equipOptionalBorder}`,
        borderRadius: dt.radiusSm,
        overflow: "hidden",
        animation: "fadeUp 0.35s ease-out both",
        animationDelay: `${idx * 80}ms`,
      }}
    >
      {/* Equipment section header */}
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          padding: dt.specs.rowPadding,
          cursor: "pointer",
          borderLeft: `${dt.specs.sectionAccentWidth} solid ${isStandard ? dt.specs.equipStandardAccent : dt.specs.equipOptionalAccent}`,
          backgroundColor: isStandard ? dt.specs.equipStandardBg : dt.specs.equipOptionalBg,
          userSelect: "none",
          "&:hover": { backgroundColor: dt.rowHover },
          transition: "background 0.15s ease",
        }}
      >
        {isStandard ? (
          <CheckCircleOutlineIcon
            sx={{ fontSize: 16, color: dt.specs.equipStandardAccent }}
          />
        ) : (
          <AddCircleOutlineIcon
            sx={{ fontSize: 16, color: dt.specs.equipOptionalAccent }}
          />
        )}
        <Box
          sx={{
            flex: 1,
            fontSize: "12px",
            fontWeight: 700,
            fontFamily: dt.font,
            color: isStandard ? dt.specs.equipStandardAccent : dt.specs.equipOptionalAccent,
            textTransform: "uppercase",
            letterSpacing: "0.4px",
          }}
        >
          {section.sectionName}
        </Box>
        <Box
          sx={{
            fontSize: "11px",
            fontFamily: dt.fontMono,
            fontWeight: 500,
            color: isStandard ? dt.specs.equipStandardAccent : dt.specs.equipOptionalAccent,
            backgroundColor: isStandard
              ? "rgba(39, 174, 96, 0.08)"
              : "rgba(125, 60, 152, 0.08)",
            borderRadius: dt.specs.pillRadius,
            padding: "2px 10px",
            lineHeight: 1.4,
            whiteSpace: "nowrap",
          }}
        >
          {countLabel}
        </Box>
        {expanded ? (
          <ExpandLessIcon sx={{ color: dt.textSecondary, fontSize: 20 }} />
        ) : (
          <ExpandMoreIcon sx={{ color: dt.textSecondary, fontSize: 20 }} />
        )}
      </Box>

      {/* Equipment items grid -- 3-tier responsive: 1 col phone, 2 col tablet, 3 col desktop */}
      {expanded && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isPhone
              ? "1fr"
              : isTablet
                ? "1fr 1fr"
                : "1fr 1fr 1fr",
            gap: 0,
            padding: 0,
          }}
        >
          {items.map((item, i) => (
            <Tooltip key={`${item}-${i}`} title={item} enterDelay={600} arrow>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "8px",
                  padding: "9px 16px",
                  backgroundColor: i % 2 === 0 ? dt.specs.rowOdd : dt.specs.rowEven,
                  borderBottom: `1px solid ${dt.rowBorder}`,
                  transition: "background 0.15s ease",
                  "&:hover": { backgroundColor: dt.rowHover },
                  minHeight: "36px",
                }}
              >
                {isStandard ? (
                  <CheckCircleOutlineIcon
                    sx={{
                      fontSize: 14,
                      color: dt.specs.equipStandardAccent,
                      flexShrink: 0,
                      opacity: 0.7,
                      mt: "2px",
                    }}
                  />
                ) : (
                  <AddCircleOutlineIcon
                    sx={{
                      fontSize: 14,
                      color: dt.specs.equipOptionalAccent,
                      flexShrink: 0,
                      opacity: 0.7,
                      mt: "2px",
                    }}
                  />
                )}
                <Box
                  sx={{
                    fontSize: "13px",
                    fontWeight: 400,
                    fontFamily: dt.font,
                    color: dt.textPrimary,
                    minWidth: 0,
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    lineHeight: 1.5,
                  }}
                >
                  {item}
                </Box>
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}
    </Box>
  );
};

/* -- Section Header ------------------------------------------------------- */
const SectionHeader: React.FC<{
  name: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  t: (k: string, o?: any) => any;
}> = ({ name, count, expanded, onToggle, t }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      padding: dt.specs.rowPadding,
      cursor: "pointer",
      borderLeft: `${dt.specs.sectionAccentWidth} solid ${dt.specs.sectionAccent}`,
      backgroundColor: dt.headerBg,
      userSelect: "none",
      "&:hover": { backgroundColor: dt.rowHover },
      transition: "background 0.15s ease",
    }}
  >
    <Box
      sx={{
        flex: 1,
        fontSize: "12px",
        fontWeight: 700,
        fontFamily: dt.font,
        color: dt.blue,
        textTransform: "uppercase",
        letterSpacing: "0.4px",
      }}
    >
      {name}
    </Box>
    <Box
      sx={{
        fontSize: "11px",
        fontFamily: dt.fontMono,
        fontWeight: 500,
        color: dt.specs.countPillText,
        backgroundColor: dt.specs.countPillBg,
        borderRadius: dt.specs.pillRadius,
        padding: "2px 8px",
        lineHeight: 1.4,
      }}
    >
      {t("specsCount", { count })}
    </Box>
    {expanded ? (
      <ExpandLessIcon sx={{ color: dt.textSecondary, fontSize: 20 }} />
    ) : (
      <ExpandMoreIcon sx={{ color: dt.textSecondary, fontSize: 20 }} />
    )}
  </Box>
);

/* -- Spec Row (Desktop) -------------------------------------------------- */
const SpecRowDesktop: React.FC<{
  spec: CatalogSpecItem;
  idx: number;
  toMetric: boolean;
}> = ({ spec, idx, toMetric }) => {
  const isNumeric = spec.numericValue !== null && spec.numericValue !== undefined;
  const converted = isNumeric
    ? convertSpec(spec.numericValue, spec.unit, toMetric)
    : null;

  const displayValue = isNumeric && converted ? converted.displayValue : spec.specValue;
  const displayUnit = isNumeric && converted ? converted.displayUnit : spec.unit;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        padding: dt.specs.rowPadding,
        backgroundColor: idx % 2 === 0 ? dt.specs.rowOdd : dt.specs.rowEven,
        transition: "background 0.15s ease",
        "&:hover": { backgroundColor: dt.rowHover },
        gap: "12px",
      }}
    >
      <Box
        sx={{
          fontSize: "13px",
          color: dt.textSecondary,
          fontFamily: dt.font,
          fontWeight: 400,
          width: "220px",
          minWidth: "220px",
          flexShrink: 0,
          textTransform: "capitalize",
        }}
      >
        {spec.specKey}
      </Box>
      <Tooltip title={displayValue || ""} enterDelay={600} arrow>
        <Box
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: dt.textPrimary,
            fontFamily: isNumeric ? dt.fontMono : dt.font,
            flex: 1,
            minWidth: 0,
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {displayValue}
          {displayUnit && (
            <Box
              component="span"
              sx={{
                fontSize: "12px",
                fontWeight: 400,
                fontFamily: dt.fontMono,
                color: dt.textMuted,
                ml: "4px",
              }}
            >
              {displayUnit}
            </Box>
          )}
        </Box>
      </Tooltip>
    </Box>
  );
};

/* -- Spec Row (Phone) ---------------------------------------------------- */
const SpecRowPhone: React.FC<{
  spec: CatalogSpecItem;
  idx: number;
  toMetric: boolean;
}> = ({ spec, idx, toMetric }) => {
  const isNumeric = spec.numericValue !== null && spec.numericValue !== undefined;
  const converted = isNumeric
    ? convertSpec(spec.numericValue, spec.unit, toMetric)
    : null;

  const displayValue = isNumeric && converted ? converted.displayValue : spec.specValue;
  const displayUnit = isNumeric && converted ? converted.displayUnit : spec.unit;

  return (
    <Box
      sx={{
        padding: "10px 16px",
        backgroundColor: idx % 2 === 0 ? dt.specs.rowOdd : dt.specs.rowEven,
      }}
    >
      <Box
        sx={{
          fontSize: "12px",
          fontWeight: 500,
          fontFamily: dt.font,
          color: dt.textMuted,
          mb: "2px",
          textTransform: "capitalize",
        }}
      >
        {spec.specKey}
      </Box>
      <Box
        sx={{
          fontSize: "14px",
          fontWeight: 500,
          fontFamily: isNumeric ? dt.fontMono : dt.font,
          color: dt.textPrimary,
          wordBreak: "break-word",
          overflowWrap: "break-word",
        }}
      >
        {displayValue}
        {displayUnit && (
          <Box
            component="span"
            sx={{
              fontSize: "12px",
              fontWeight: 400,
              fontFamily: dt.fontMono,
              color: dt.textMuted,
              ml: "4px",
            }}
          >
            {displayUnit}
          </Box>
        )}
      </Box>
    </Box>
  );
};

/* -- Toggle Pill --------------------------------------------------------- */
const TogglePill: React.FC<{
  options: { label: string; value: string }[];
  selected: string;
  onSelect: (v: string) => void;
}> = ({ options, selected, onSelect }) => (
  <Box
    sx={{
      display: "inline-flex",
      border: `1px solid ${dt.specs.pillBorder}`,
      borderRadius: dt.specs.pillRadius,
      overflow: "hidden",
    }}
  >
    {options.map((opt) => (
      <Box
        key={opt.value}
        onClick={() => onSelect(opt.value)}
        sx={{
          padding: "4px 12px",
          fontSize: "12px",
          fontWeight: 600,
          fontFamily: dt.font,
          cursor: "pointer",
          transition: "all 0.2s ease",
          backgroundColor:
            selected === opt.value ? dt.specs.pillBg : dt.specs.pillInactiveBg,
          color:
            selected === opt.value ? dt.specs.pillText : dt.specs.pillInactiveText,
          userSelect: "none",
          "&:hover": {
            backgroundColor:
              selected === opt.value ? dt.specs.pillBg : dt.blueGlow,
          },
        }}
      >
        {opt.label}
      </Box>
    ))}
  </Box>
);

/* -- Group Divider (enhanced with category-specific accents and icons) --- */
const GroupDivider: React.FC<{ label: string; groupKey: GroupKey }> = ({ label, groupKey }) => {
  const colors = getGroupColors(groupKey);
  const icon = getGroupIcon(groupKey);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mt: 3,
        mb: 1.5,
        mx: 0,
        padding: "8px 12px",
        borderRadius: dt.radiusSm,
        backgroundColor: colors.bg,
        borderLeft: `3px solid ${colors.accent}`,
      }}
    >
      {icon && (
        <Box sx={{ color: colors.accent, display: "flex", alignItems: "center" }}>
          {icon}
        </Box>
      )}
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          fontFamily: dt.font,
          color: colors.accent,
          textTransform: "uppercase",
          letterSpacing: "1.2px",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          flex: 1,
          height: "1px",
          background: `linear-gradient(90deg, ${colors.accent}40, transparent)`,
          ml: 0.5,
        }}
      />
    </Box>
  );
};

/* -- Regular Spec Section ------------------------------------------------ */
const RegularSpecSection: React.FC<{
  section: CatalogSpecSection;
  sIdx: number;
  isPhone: boolean;
  toMetric: boolean;
  expanded: boolean;
  onToggle: () => void;
  t: (k: string, o?: any) => any;
}> = ({ section, sIdx, isPhone, toMetric, expanded, onToggle, t }) => (
  <Box
    sx={{
      mb: 1.5,
      border: `1px solid ${dt.cardBorder}`,
      borderRadius: dt.radiusSm,
      overflow: "hidden",
      animation: "fadeUp 0.35s ease-out both",
      animationDelay: `${sIdx * 80}ms`,
    }}
  >
    <SectionHeader
      name={section.sectionName}
      count={section.specs.length}
      expanded={expanded}
      onToggle={onToggle}
      t={t}
    />
    {expanded && (
      <Box>
        {section.specs.map((spec: CatalogSpecItem, idx: number) =>
          isPhone ? (
            <SpecRowPhone
              key={`${section.sectionName}-${spec.specKey}-${idx}`}
              spec={spec}
              idx={idx}
              toMetric={toMetric}
            />
          ) : (
            <SpecRowDesktop
              key={`${section.sectionName}-${spec.specKey}-${idx}`}
              spec={spec}
              idx={idx}
              toMetric={toMetric}
            />
          ),
        )}
      </Box>
    )}
  </Box>
);

/* -- Main Component ------------------------------------------------------ */
const MachineSpecs: React.FC<MachineSpecsProps> = ({ catalogModelId }) => {
  const dispatch = useAppDispatch();
  const { translate: t, getCurrentLanguage } = useTranslation("myMachinery");
  const isPhone = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width:768px) and (max-width:1024px)");
  useRedesignSetup();

  const currentLang = getCurrentLanguage(); // "en" or "sk"

  const { selectedModel, selectedModelLoading } =
    useAppSelector<MachineCatalogState>((state: any) => state.machineCatalog);

  const [unitSystem, setUnitSystem] = useState<"metric" | "imperial">("metric");
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  // Only fetch if the model isn't already loaded (avoids duplicate fetch when
  // CatalogModelDetailPage already dispatched getCatalogModelDetail).
  useEffect(() => {
    if (catalogModelId && selectedModel?.id !== catalogModelId) {
      dispatch(getCatalogModelDetail(catalogModelId));
    }
    // NOTE: Do NOT clearSelectedModel on unmount here — the parent page
    // (CatalogModelDetailPage) owns the lifecycle of selectedModel.
    // Clearing here caused a race condition where MachineSpecs unmount
    // during loading would null out the model, showing "Model not found".
  }, [catalogModelId, dispatch, selectedModel?.id]);

  // Classify and sort sections
  const { resolvedDescription, regularSections, equipmentSections, usedEncodingFallback } = useMemo(() => {
    const allSections = (selectedModel?.specSections || []).filter(
      (s) => s.specs.length > 0,
    );

    const desc: CatalogSpecSection[] = [];
    const regular: CatalogSpecSection[] = [];
    const equip: CatalogSpecSection[] = [];

    for (const section of allSections) {
      // Bug 6 fix: Also detect description-like sections via heuristic
      if (isDescriptionSection(section.sectionName) || isDescriptionLikeSection(section)) {
        desc.push(section);
      } else if (isEquipmentSection(section.sectionName)) {
        equip.push(section);
      } else {
        regular.push(section);
      }
    }

    // Sort each group by the hierarchy order
    desc.sort((a, b) => getSectionOrder(a.sectionName) - getSectionOrder(b.sectionName));
    regular.sort((a, b) => getSectionOrder(a.sectionName) - getSectionOrder(b.sectionName));
    equip.sort((a, b) => getSectionOrder(a.sectionName) - getSectionOrder(b.sectionName));

    /* ---- Language-aware description resolution ----------------------------
     *
     * Data structure: Descriptions can appear in two formats:
     *   A) SectionName = "Description (EN)" / "Description (CZ)" — language in section name
     *   B) SectionName = "Description" with SpecKey = "Description (EN)" / "Description (CZ)"
     *      — language tag is in the spec key, not the section name
     *
     * Strategy:
     * - Extract EN and CZ text from BOTH formats.
     * - If language is "en" -> show EN description only. Never fall back to CZ.
     * - If language is "sk" -> prefer CZ description. If CZ has mojibake or is
     *   empty, fall back to EN.
     * - Machine-type description sections are filtered: skip any with mojibake,
     *   and when language is "en" also skip any that look like CZ text.
     */

    // Helper: get text from an array of specs
    const specsToText = (specs: CatalogSpecItem[]): string =>
      specs.map((s) => s.specValue).filter(Boolean).join("\n\n").trim();

    // Format A: section names contain language tag
    const enSectionA = desc.find(
      (s) => s.sectionName.toLowerCase() === "description (en)",
    );
    const czSectionA = desc.find(
      (s) => s.sectionName.toLowerCase() === "description (cz)",
    );

    // Format B: section named "Description" with specKey containing language tag
    const genericDescSection = desc.find(
      (s) => s.sectionName.toLowerCase() === "description",
    );
    const enSpecsB = genericDescSection
      ? genericDescSection.specs.filter((s) =>
          s.specKey?.toLowerCase().includes("(en)"),
        )
      : [];
    const czSpecsB = genericDescSection
      ? genericDescSection.specs.filter((s) =>
          s.specKey?.toLowerCase().includes("(cz)"),
        )
      : [];

    // Merge text from both formats
    const enText = specsToText(enSectionA?.specs || []) || specsToText(enSpecsB);
    const czText = specsToText(czSectionA?.specs || []) || specsToText(czSpecsB);
    const czHasMojibake = czText ? hasMojibake(czText) : false;

    let primaryDescText = "";
    let didFallbackToEn = false;
    if (currentLang === "sk") {
      // Slovak: prefer CZ, fall back to EN if CZ has mojibake or is empty
      if (czHasMojibake || !czText) {
        primaryDescText = enText;
        // Only flag as fallback if there WAS Czech text but it was garbled
        didFallbackToEn = czHasMojibake && enText.length > 0;
      } else {
        primaryDescText = czText;
      }
    } else {
      // English: show EN text only — never fall back to CZ
      primaryDescText = enText;
    }

    // Machine-type description sections: everything that isn't a recognized
    // EN/CZ description section or the generic "Description" section
    const machineTypeSections = desc.filter(
      (s) =>
        s.sectionName.toLowerCase() !== "description (en)" &&
        s.sectionName.toLowerCase() !== "description (cz)" &&
        s.sectionName.toLowerCase() !== "description",
    );

    // Build machine-type description texts, filtering out mojibake and
    // language-inappropriate content.
    const machineTypeDescriptions = machineTypeSections.map((s) => ({
      label: s.sectionName,
      text: specsToText(s.specs),
    })).filter((d) => {
      if (d.text.length === 0) return false;
      // Always filter out mojibake text — it's unreadable in any language
      if (hasMojibake(d.text)) return false;
      // When language is English, also filter out Czech-language text
      if (currentLang === "en" && isCzechText(d.text)) return false;
      return true;
    });

    return {
      resolvedDescription: {
        primaryText: primaryDescText,
        machineTypeDescriptions,
      },
      regularSections: regular,
      equipmentSections: equip,
      usedEncodingFallback: didFallbackToEn,
    };
  }, [selectedModel, currentLang]);

  // Combine all visible sections (for expand/collapse state)
  const expandableSections = useMemo(
    () => [...regularSections, ...equipmentSections],
    [regularSections, equipmentSections],
  );

  // All sections expanded by default
  useEffect(() => {
    if (expandableSections.length > 0 && !initialized) {
      setExpandedSections(new Set(expandableSections.map((s) => s.sectionName)));
      setInitialized(true);
    }
  }, [expandableSections, initialized]);

  const allExpanded =
    expandableSections.length > 0 &&
    expandableSections.every((s) => expandedSections.has(s.sectionName));

  const toggleSection = (name: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleAll = () => {
    if (allExpanded) {
      setExpandedSections(new Set());
    } else {
      setExpandedSections(new Set(expandableSections.map((s) => s.sectionName)));
    }
  };

  // --- Loading state ---
  if (selectedModelLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
        <CircularProgress size={28} sx={{ color: dt.blue }} />
      </Box>
    );
  }

  if (!selectedModel) return null;

  const hasAnyContent =
    resolvedDescription.primaryText.length > 0 ||
    resolvedDescription.machineTypeDescriptions.length > 0 ||
    regularSections.length > 0 ||
    equipmentSections.length > 0;

  if (!hasAnyContent) {
    return (
      <Box
        sx={{
          py: 4,
          textAlign: "center",
          color: dt.textMuted,
          fontSize: "13px",
          fontFamily: dt.font,
        }}
      >
        {t("noSpecifications")}
      </Box>
    );
  }

  const toMetric = unitSystem === "metric";

  // Track which group labels have been rendered
  const renderedGroups = new Set<string>();

  /** Renders a group divider if this is the first section in a new group */
  const maybeRenderGroupDivider = (sectionName: string): React.ReactNode => {
    const groupLabel = getSectionGroupLabel(sectionName, t);
    const groupKey = getSectionGroupKey(sectionName);
    if (!groupLabel || renderedGroups.has(groupLabel)) return null;
    renderedGroups.add(groupLabel);
    return <GroupDivider key={`group-${groupLabel}`} label={groupLabel} groupKey={groupKey} />;
  };

  return (
    <Box>
      {/* Toolbar: Metric/Imperial + Expand/Collapse All */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1.5,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box
          onClick={toggleAll}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 500,
            fontFamily: dt.font,
            color: dt.textLink,
            userSelect: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {allExpanded ? (
            <UnfoldLessIcon sx={{ fontSize: 16 }} />
          ) : (
            <UnfoldMoreIcon sx={{ fontSize: 16 }} />
          )}
          {allExpanded ? t("collapseAll") : t("expandAll")}
        </Box>
        <TogglePill
          options={[
            { label: t("metric"), value: "metric" },
            { label: t("imperial"), value: "imperial" },
          ]}
          selected={unitSystem}
          onSelect={(v) => setUnitSystem(v as "metric" | "imperial")}
        />
      </Box>

      {/* -- 1. Description section (language-resolved, single block) --------- */}
      {resolvedDescription.primaryText && (
        <Box sx={{ mb: 1 }}>
          <DescriptionBlock
            descriptionText={resolvedDescription.primaryText}
            sectionLabel={t("specDescription")}
            idx={0}
            showEncodingFallback={usedEncodingFallback}
            t={t}
          />
        </Box>
      )}

      {/* -- 1b. Machine-type description sections (always shown as prose) ---- */}
      {resolvedDescription.machineTypeDescriptions.length > 0 && (
        <Box sx={{ mb: 1 }}>
          {resolvedDescription.machineTypeDescriptions.map((d, idx) => (
            <DescriptionBlock
              key={d.label}
              descriptionText={d.text}
              sectionLabel={d.label}
              idx={idx + 1}
              t={t}
            />
          ))}
        </Box>
      )}

      {/* -- 2. Regular spec sections (with group dividers) ------------------- */}
      {regularSections.map((section, sIdx) => {
        const isExpanded = expandedSections.has(section.sectionName);
        return (
          <React.Fragment key={section.sectionName}>
            {maybeRenderGroupDivider(section.sectionName)}
            <RegularSpecSection
              section={section}
              sIdx={sIdx}
              isPhone={isPhone}
              toMetric={toMetric}
              expanded={isExpanded}
              onToggle={() => toggleSection(section.sectionName)}
              t={t}
            />
          </React.Fragment>
        );
      })}

      {/* -- 3. Equipment sections (list-style rendering) --------------------- */}
      {equipmentSections.length > 0 && (
        <>
          {maybeRenderGroupDivider(equipmentSections[0].sectionName)}
          {equipmentSections.map((section, idx) => (
            <EquipmentList
              key={section.sectionName}
              section={section}
              idx={regularSections.length + idx}
              isPhone={isPhone}
              isTablet={isTablet}
              t={t}
            />
          ))}
        </>
      )}
    </Box>
  );
};

export default MachineSpecs;
